import { useEffect, useState } from 'react'
import { Alert, Button, Group, Stack, TextInput } from '@mantine/core'
import { IconAlertTriangle, IconRobot } from '@tabler/icons-react'
import { isAxiosError } from 'axios'
import { useQueryClient } from '@tanstack/react-query'

import { nomearMeuRobo, reciboDoDono, type AvisoDoAjuste, type Robo } from '@/api/robo'
import { LIMITE_NOME, mensagemDoNome } from './nomeDoRobo'
import { ReciboDoAviso } from './ReciboDoAviso'
import { rotuloDoRobo } from './avisoDoAjuste'

/**
 * O valor inicial do campo. Robô ainda não batizado cujo nome de inventário
 * não serve de nome (o cadastro pode ter posto o MAC) abre VAZIO: pré-preencher
 * fazia o campo nascer em vermelho, com o Salvar travado, antes de o dono
 * digitar qualquer coisa (robô do Felipe, produção, 28/09/2026).
 */
function nomeInicial(robo: Robo): string {
  return !robo.owner_named_at && mensagemDoNome(robo.name) ? '' : robo.name
}

/**
 * O nome do robô: o mesmo campo e a mesma validação no passo Nome do wizard
 * de recebimento e na seção `#nome` da tela única (spec 2026-09-27, 4.4/4.5).
 */
export function NomeDoMeuRobo({ robo }: { robo: Robo }) {
  const qc = useQueryClient()
  const [nome, setNome] = useState(() => nomeInicial(robo))
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [salvo, setSalvo] = useState(false)
  // A reação do robô ao último salvamento (spec 4.7), com o recibo lido do ack.
  const [aviso, setAviso] = useState<AvisoDoAjuste | null>(null)
  // O nome que o PUT acabou de gravar: até o refetch trazer o robô novo, a
  // prop ainda tem o antigo, e o recibo não pode sumir por isso.
  const [salvoComo, setSalvoComo] = useState<string | null>(null)

  // Só nome e carimbo mudam o valor inicial; o objeto `robo` é novo a cada refetch.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setNome(nomeInicial(robo)) }, [robo.name, robo.owner_named_at])
  // Três estados, não dois: robô recém-resetado (`name === ''`, TASK-11959), robô com
  // nome de inventário que não serve de nome (o cadastro pôs o MAC), e o comum. O vazio
  // sai ANTES porque `mensagemDoNome('')` responde `'O nome não pode ficar em branco.'`
  // e cairia no ramo do inventário — dizendo "quem o cadastrou o registrou como ****".
  const semNome = robo.name.trim() === ''
  const inventarioInvalido = !robo.owner_named_at && !semNome && !!mensagemDoNome(robo.name)

  const problema = mensagemDoNome(nome)
  const mudou = nome.trim() !== robo.name && nome.trim() !== salvoComo
  // Aceitar o nome herdado também é batizar: o botão vale sem mudança enquanto não há carimbo.
  const podeSalvar = !problema && (mudou || !robo.owner_named_at)

  const salvar = async () => {
    setErro(null)
    setSalvo(false)
    setAviso(null)
    setSalvando(true)
    try {
      const salvoAgora = await nomearMeuRobo(nome.trim())
      await qc.invalidateQueries({ queryKey: ['meu-robo'] })
      setSalvoComo(nome.trim())
      setSalvo(true)
      setAviso(salvoAgora.aviso ?? null)
    } catch (err) {
      // O 422 do backend continua sendo quem decide. A validação local só
      // existe para dar mensagem melhor ANTES de chegar aqui.
      setErro(isAxiosError(err) && err.response?.status === 422
        ? 'O Console recusou esse nome. Use letras, espaço, apóstrofo e hífen, começando por letra.'
        : 'Não foi possível salvar o nome agora.')
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Stack gap="md">
      {!robo.owner_named_at && (
        <Alert color="blue" icon={<IconRobot size={18} />}>
          {semNome
            ? <>Este robô voltou ao estado de fábrica e ainda não tem nome. Dê a ele o
                nome pelo qual você vai chamá-lo, e o chat abre.</>
            : inventarioInvalido
              ? <>Este robô ainda não foi batizado. Quem o cadastrou o registrou
                  como <b>{robo.name}</b>, que não serve de nome — dê a ele o nome
                  pelo qual você vai chamá-lo, e o chat abre.</>
              : <>Este robô ainda não foi batizado. O nome abaixo é o de inventário,
                  posto por quem o cadastrou — dê a ele o nome pelo qual você vai
                  chamá-lo, e o chat abre.</>}
        </Alert>
      )}
      {erro && <Alert color="red" icon={<IconAlertTriangle size={18} />}>{erro}</Alert>}
      {salvo && !mudou && <Alert color="green">Nome salvo.</Alert>}
      {salvo && !mudou && aviso && (
        <ReciboDoAviso
          key={aviso.id ?? aviso.estado}
          aviso={aviso}
          caminho={aviso.id ? reciboDoDono(aviso.id) : null}
          robo={rotuloDoRobo(robo.hardware_model)}
        />
      )}
      <Group align="flex-end" wrap="nowrap">
        <TextInput
          flex={1}
          label="Nome do robô"
          description={`Como você chama ele. No máximo ${LIMITE_NOME} caracteres, começando por letra.`}
          maxLength={LIMITE_NOME * 2}
          value={nome}
          error={nome.length > 0 ? problema : null}
          onChange={(ev) => { setNome(ev.currentTarget.value); setSalvo(false) }}
        />
        <Button disabled={!podeSalvar} loading={salvando} onClick={salvar}>
          Salvar
        </Button>
      </Group>
    </Stack>
  )
}
